import React, { useEffect, useState } from 'react'
import {
  makeStyles,
  createStyles,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  InputLabel,
  InputAdornment,
  IconButton,
} from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { changePassword, signOut, getUserInfo } from 'src/store/slices/authSlice'
import { oriSchema } from 'src/utils/schema'
import { useTranslateFormErrors } from 'src/hooks'
import FocusError from 'src/mobile/components/FocusError'
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined'
import VisibilityOffOutlined from '@material-ui/icons/VisibilityOffOutlined'
const useStyles = makeStyles((theme) =>
  createStyles({
    form: {
      backgroundColor: theme.palette.background.paper,
      paddingTop: theme.spacing(1),
      // paddingLeft: theme.spacing(2),
    },
    textField: {
      marginBottom: theme.spacing(1.5),
    },
    lastTextField: {
      marginBottom: theme.spacing(2),
    },
    popperHelperText: {
      color: '#9CA3AF',
    },
    helperText: {
      marginLeft: 0,
      marginBottom: theme.spacing(0.5),
    },
  }),
)

const initialValues = {
  password: '',
  newPassword: '',
  confirmPassword: '',
}

const ProfileAccount = ({ onConfirm }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { t, i18n } = useTranslation()

  const { changePasswordStatus } = useSelector((state) => state.auth)
  const schema = oriSchema(t).pick(['password', 'newPassword', 'confirmPassword'])

  const [showPassword, setShowPassword] = useState(false)
  const handleClickShowPassword = () => setShowPassword((show) => !show)

  useEffect(() => {
    dispatch(getUserInfo())
  }, [])
  const handleCancel = (values) => {
    onConfirm && onConfirm(values)
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values) => {
        if (changePasswordStatus === 'pending') return
        const { password, newPassword } = values
        await dispatch(changePassword({ password, newPassword })).unwrap()
        dispatch(signOut())
        history.replace(`/${i18n.language}/signin`)

        toast.success(t('tips.change_password_successfully'))
      }}
    >
      {(props) => {
        const { values, setFieldTouched, handleSubmit, handleChange, touched, errors } = props
        const isError = (field) => touched[field] && Boolean(errors[field])
        const errorText = (field) => touched[field] && errors[field]
        useTranslateFormErrors(errors, touched, setFieldTouched)
        return (
          <form onSubmit={handleSubmit} noValidate>
            <Box className={classes.form}>
              <InputLabel>{t('form.old_password')}</InputLabel>
              <TextField
                className={classes.textField}
                id="password-field"
                name="password"
                margin="normal"
                fullWidth
                value={values.password}
                onChange={handleChange}
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                autoComplete="true"
                error={isError('password')}
                required
                helperText={errorText('password')}
                FormHelperTextProps={{
                  classes: {
                    root: classes.helperText,
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {!showPassword ? (
                          <VisibilityOffOutlined color="primary" />
                        ) : (
                          <VisibilityOutlined color="primary" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <InputLabel>{t('form.new_password')}</InputLabel>
              <TextField
                className={isError('newPassword') && classes.lastTextField}
                id="new-password-field"
                name="newPassword"
                margin="normal"
                fullWidth
                value={values.newPassword}
                onChange={handleChange}
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                error={isError('newPassword')}
                required
                helperText={errorText('newPassword')}
                FormHelperTextProps={{
                  classes: {
                    root: classes.helperText,
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <Typography variant="caption" className={classes.popperHelperText}>
                {t('validation.password_match', { field: '$t(form.password)' })}
              </Typography>
              <Box mt={3}>
                <InputLabel>{t('form.confirm_password')}</InputLabel>
                <TextField
                  className={isError('confirmPassword') && classes.lastTextField}
                  margin="normal"
                  id="confirm-password-field"
                  name="confirmPassword"
                  fullWidth
                  variant="outlined"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  type={showPassword ? 'text' : 'password'}
                  error={isError('confirmPassword')}
                  required
                  helperText={errorText('confirmPassword')}
                  FormHelperTextProps={{
                    classes: {
                      root: classes.helperText,
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            </Box>
            <Box mt={5} mb={2}>
              <Button fullWidth type="submit" variant="contained" color="secondary">
                {changePasswordStatus === 'pending' ? (
                  <CircularProgress color="inherit" size={24} />
                ) : (
                  t('common.save_change')
                )}
              </Button>
            </Box>
            <Button variant="outlined" fullWidth color="default" onClick={handleCancel}>
              {t('common.cancel')}
            </Button>

            <FocusError />
          </form>
        )
      }}
    </Formik>
  )
}

export default ProfileAccount
